import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import {
  Headset, LayoutDashboard, TicketCheck, PhoneCall, LogOut, ChevronLeft, BarChart3, Users, Menu
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import NotificationBell from "@/components/NotificationBell";
import MobileBottomNav from "@/components/MobileBottomNav";

const navItems = [
  { to: "/support-panel", icon: LayoutDashboard, label: "لوحة التحكم", end: true },
  { to: "/support-panel/tickets", icon: TicketCheck, label: "التذاكر" },
  { to: "/support-panel/users", icon: Users, label: "إدارة المستخدمين" },
  { to: "/support-panel/calls", icon: PhoneCall, label: "سجل المكالمات" },
  { to: "/support-panel/stats", icon: BarChart3, label: "الإحصائيات" },
];

const SidebarBody = ({ user, profile, roles, signOut, navigate, onNavigate }: any) => (
  <>
    <div className="p-4 border-b border-teal-700">
      <h1 className="text-lg font-bold flex items-center gap-2">
        <Headset className="w-5 h-5 text-teal-300" />
        لوحة الدعم الفني
      </h1>
      <p className="text-xs text-teal-400 mt-1 truncate">{profile?.full_name || user?.email}</p>
      <p className="text-xs text-teal-500 mt-0.5">
        {roles.includes("support") && "دعم فني"}
        {roles.includes("call_center") && "كول سنتر"}
        {roles.includes("super_admin") && "مشرف عام"}
      </p>
    </div>

    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
              isActive
                ? "bg-teal-500/20 text-teal-200 font-medium"
                : "text-teal-300 hover:bg-teal-800 hover:text-white"
            )
          }
        >
          <item.icon className="w-4 h-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>

    <div className="p-3 border-t border-teal-700 space-y-2">
      {roles.includes("super_admin") && (
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-teal-300 hover:text-white hover:bg-teal-800"
          onClick={() => { navigate("/admin"); onNavigate?.(); }}
        >
          <ChevronLeft className="w-4 h-4" />
          لوحة المشرف العام
        </Button>
      )}
      <Button
        variant="ghost"
        className="w-full justify-start gap-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
        onClick={signOut}
      >
        <LogOut className="w-4 h-4" />
        تسجيل خروج
      </Button>
    </div>
  </>
);

const SupportLayout = () => {
  const { roles, loading, user, signOut, profile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const hasAccess = roles.includes("support") || roles.includes("call_center") || roles.includes("super_admin");

  useEffect(() => {
    if (!loading && !hasAccess) {
      navigate("/dashboard");
    }
  }, [loading, hasAccess, navigate]);

  if (loading || !hasAccess) return null;

  return (
    <div className="min-h-screen flex" dir="rtl">
      <aside className="w-64 bg-teal-900 text-white hidden md:flex flex-col">
        <SidebarBody user={user} profile={profile} roles={roles} signOut={signOut} navigate={navigate} />
      </aside>

      <div className="flex-1 flex flex-col overflow-auto min-w-0">
        <header className="flex items-center justify-between gap-2 p-3 sm:p-4 border-b bg-background sticky top-0 z-20">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="القائمة">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0 w-72 bg-teal-900 text-white border-teal-700">
              <div className="flex flex-col h-full">
                <SidebarBody user={user} profile={profile} roles={roles} signOut={signOut} navigate={navigate} onNavigate={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>

          <h2 className="md:hidden text-sm font-semibold flex-1 text-center">الدعم الفني</h2>

          <NotificationBell />
        </header>
        <main className="app-main flex-1 p-4 sm:p-6 overflow-auto has-bottom-nav md:!pb-6">
          <Outlet />
        </main>
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default SupportLayout;
